interface PendingEntry {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
}

interface WorkerResponse {
  reqId: number;
  ok: boolean;
  error?: string;
  poses?: {
    landmarks: {
      x: number;
      y: number;
      score: number;
      worldX?: number;
      worldY?: number;
      worldZ?: number;
      worldVisibility?: number;
    }[];
  }[];
  inferMs?: number;
}

export class PoseWorkerClient {
  private worker: Worker | null = null;
  private reqId = 0;
  private readonly pending = new Map<number, PendingEntry>();

  start(): void {
    if (this.worker) return;
    this.worker = new Worker(new URL('../workers/pose.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (ev: MessageEvent<WorkerResponse>) => this.handleMessage(ev.data);
    this.worker.onerror = (err) => {
      console.error('[PoseWorkerClient] worker error', err);
      for (const entry of this.pending.values()) entry.reject(new Error('worker error'));
      this.pending.clear();
    };
  }

  async request(msg: Record<string, unknown>, transfer: Transferable[] = []): Promise<unknown> {
    if (!this.worker) throw new Error('worker not running');
    const id = ++this.reqId;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage({ ...msg, reqId: id }, transfer);
    });
  }

  terminate(): void {
    if (!this.worker) return;
    const w = this.worker;
    this.worker = null;
    for (const entry of this.pending.values()) entry.reject(new Error('disposed'));
    this.pending.clear();
    try {
      w.postMessage({ type: 'dispose', reqId: 0 });
    } catch {
      // ignore
    }
    w.terminate();
  }

  private handleMessage(data: WorkerResponse): void {
    const entry = this.pending.get(data.reqId);
    if (!entry) return;
    this.pending.delete(data.reqId);
    if (data.ok) entry.resolve(data);
    else entry.reject(new Error(data.error ?? 'worker error'));
  }
}
